<template>
  <CardWithTitle
    :item-id="itemId"
    :request-status="requestStatus"
    :options-data="cardOptionsWithCSVExportOption"
    :has-not-classified="hasNotClassified"
    @call-option-menu="callOptionMenu"
    @reload="$emit('reload', $event)"
    @on-show-not-classified="onShowNotClassified"
  >
    <v-row
      no-gutters
      class="pie-chart-container"
    >
      <div class="pie-chart">
        <div class="pie-chart__chart-area">
          <HighCharts
            v-if="series.length"
            :ref="chartRef"
            :key="chartRef"
            :options="chartOptions"
            :callback="highchartsCallBack"
            class="pie-chart__highchart"
          />
        </div>
        <div class="pie-chart__aggregation">
          <h3 class="pie-chart__title">
            {{ sumSeries.toLocaleString('pt-BR') }}
          </h3>
          <p class="pie-chart__subtitle">
            {{ aggregationText }}
          </p>
        </div>
      </div>
      <ChartLegendTable
        :legend-data="localizedLegendData"
        :show-interval="showInterval"
        show-new-style
        show-legend-value
        show-legend-percent
      />
    </v-row>

    <template #top-right-slot>
      <template v-if="hasCustomFilter">
        <DsButton
          v-tooltip="filterButton"
          color="tertiary"
          :name="nameIcon"
          :icon="true"
          @click="openFilter"
        />
      </template>
      <Badge
        v-if="hasFilterApplied && isEditor"
        type="info"
        :tooltip="$t('components.Badge.label.tooltip.info')"
        :value="$t('components.Badge.label.icon.key')"
        :preset="true"
      />
    </template>
  </CardWithTitle>
</template>

<script>
import { Chart } from 'highcharts-vue';
import highChartsBasics from '@/mixins/highcharts-basics';
import cardsModel from '@/mixins/cards-model';
import exportModel from '@/mixins/export-model';
import sentimentModel from '@/mixins/sentiment-model';
import merge from 'lodash/merge';
import CardWithTitle from '@/components/cards/models/CardWithTitle';
import Badge from '@/components/Badge';
import ChartLegendTable from '@/components/cards/models/ChartLegendTable';
import { DsButton } from '@ds/components';

export default {
  name: 'PieChart',
  components: {
    HighCharts: Chart,
    CardWithTitle,
    Badge,
    ChartLegendTable,
    DsButton,
  },
  mixins: [
    highChartsBasics,
    cardsModel,
    exportModel,
    sentimentModel,
  ],
  props: {
    requestStatus: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    isPolarity: {
      type: Boolean,
      default: false,
    },
    series: {
      type: [Array, Object],
      default: () => ([]),
    },
    cardOptions: {
      type: [Array, Object],
      default: () => ([]),
    },
    fieldLabel: {
      type: String,
      default: null,
    },
    showInterval: {
      type: Boolean,
      default: false,
    },
    hasCustomFilter: {
      type: Boolean,
      default: false,
    },
    openFilter: {
      type: Function,
      default: () => {},
    },
    nameIcon: {
      type: String,
      default: 'filter',
    },
    filterButton: {
      type: String,
      default: 'Aplicar Filtro',
    },
  },
  data() {
    return {
      hideNotClassified: true,
      legendData: null,
    };
  },
  computed: {
    notClassifiedLabels() {
      return [
        this.$t('mixins.sentimentModel.notClassified'),
        this.$t('mixins.genderModel.label.notClassified'),
        'Não Classificados', 'Não classificado',
      ];
    },
    localizedLegendData() {
      if (!this.legendData || !this.legendData.length) return this.legendData || [];
      return this.legendData.map(point => {
        const matchingSeries = this.seriesData.find(s => s.color === point.color);
        return {
          ...point,
          name: matchingSeries ? matchingSeries.name : point.name,
          setVisible: point.setVisible ? point.setVisible.bind(point) : undefined,
          setState: point.setState ? point.setState.bind(point) : undefined,
        };
      });
    },
    notShowClassifiedLabel() {
      return this.$t('components.models.PieChart.label.notShowClassifiedLabel');
    },
    showClassifiedLabel() {
      return this.$t('components.models.PieChart.label.showClassifiedLabel');
    },
    polarityOrder() {
      return {
        [this.$t('mixins.sentimentModel.positive')]: 1,
        [this.$t('mixins.sentimentModel.neutral')]: 2,
        [this.$t('mixins.sentimentModel.negative')]: 3,
        [this.$t('mixins.sentimentModel.notClassified')]: 4,
      };
    },
    seriesData() {
      if (!this.isPolarity) {
        return [...this.getFormattedSeries()].sort((actualValue, prevValue) => {
          if (actualValue.y === prevValue.y) return 0;
          return actualValue.y > prevValue.y ? -1 : 1;
        });
      }
      return [...this.getFormattedSeries()].sort((actualValue, prevValue) => this.sortSeries(actualValue, prevValue));
    },
    chartOptions() {
      const customOptions = {
        chart: {
          type: 'pie',
          plotBackgroundColor: null,
          plotBorderWidth: 0,
          plotShadow: false,
          spacing: [0, 0, 0, 0],
          margin: [0, 0, 0, 0],
        },
        plotOptions: {
          pie: {
            borderColor: 'black',
            borderWidth: 0.5,
            showInLegend: false,
          },
        },
        tooltip: {
          enabled: false,
        },
        series: [{
          data: this.seriesData,
          innerSize: '65%',
          dataLabels: {
            enabled: false,
          },
        }],
      };
      return merge({}, this.highchartsBasics, this.highchartTheme, customOptions, this.cardOptions, { legend: { enabled: false } });
    },
    hasNotClassified() {
      return Boolean(this.series.find(series => this.notClassifiedLabels.includes(series.name)));
    },
    sumSeries() {
      if (this.showInterval) {
        return this.series.find(serie => serie.interval_avg !== undefined)?.interval_avg || 0;
      }
      return this.series.
        filter(serie => this.hideNotClassified ? !this.notClassifiedLabels.includes(serie.name) : true).
        reduce((result, { y }) => result + y, 0);
    },
    aggregationText() {
      const aggregationKey = `components.models.PieChart.aggregationType.${this.card.dataType}`;
      const fieldName = this.fieldLabel || (
        this.$te(aggregationKey)
          ? this.$t(aggregationKey)
          : this.$t('components.models.PieChart.aggregationType.default')
      );
      if (this.showInterval) {
        return `${this.$t('components.models.PieChart.label.aggregationTextAvgSAC')}`;
      }
      return `${this.$t('components.models.PieChart.label.aggregationText')} ${fieldName}`;
    },
  },
  watch: {
    chartOptions() {
      this.$nextTick(() => {
        this.highchartsCallBack(this.$refs[this.chartRef].chart);
      });
    },
  },
  methods: {
    getFormattedSeries() {
      let series = this.series;
      if (this.hasNotClassified && this.hideNotClassified) {
        series = series.filter(s => !this.notClassifiedLabels.includes(s.name));
      }
      if (this.isPolarity) {
        return series.map(serie => ({ ...serie, name: this.getPolarityLabel(serie.name) }));
      }
      return series;
    },
    getPolarityLabel(name) {
      const pluralNotClassified = ['Não Classificados', 'Not Classified', 'No Clasificados'];
      if (pluralNotClassified.includes(name)) {
        return this.$t('mixins.sentimentModel.notClassified');
      }
      return this.getSentimentLabel(name);
    },
    onShowNotClassified() {
      this.hideNotClassified = !this.hideNotClassified;
    },
    sortSeries(actualValue, prevValue) {
      if (this.polarityOrder[actualValue.name] === this.polarityOrder[prevValue.name]) return 0;
      return this.polarityOrder[actualValue.name] < this.polarityOrder[prevValue.name] ? -1 : 1;
    },
    highchartsCallBack(chart) {
      this.legendData = chart?.series[0]?.data.filter(serie => Boolean(serie.y));
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '~vuetify/src/styles/main.sass';
  $legend-height: 72px;

  .pie-chart-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    overflow: hidden;
    justify-content: flex-start;
  }

  .pie-chart {
    display: flex;
    width: 100%;
    height: calc(100% - #{$legend-height});
    font-weight: var(--ds-font-weight-regular);

    &__chart-area {
      min-width: 149px;
      min-height: 133px;
      width: 40%;
    }

    &__highchart {
      height: 100%;
    }

    &__aggregation {
      display: flex;
      flex-direction: column;
      justify-content: center;
      margin-left: var(--ds-spacing-stack-xs);
    }

    &__title {
      color: var(--ds-color-text-1);
      font-size: var(--ds-font-size-md);
      font-weight: var(--ds-font-weight-semibold);
    }

    &__subtitle {
      color: var(--ds-color-text-2);
      font-size: var(--ds-font-size-xs);
      line-height: var(--ds-font-line-height-slim);
    }

    &--with-not-classified {
      height: calc(100% - 36px) !important;
    }

    &__data-label {
      font-size: 12px;
    }
  }

  ::v-deep {
    .highcharts-container,
    svg {
      overflow: visible !important;
    }
  }
</style>
